/**
 * Authentication gate for the authenticated route tree. Reads the
 * `<AuthSessionContext>` and renders one of three things:
 *
 *   1. `<BootSplash />` — chrome-less, while the SDK is resolving
 *      initial auth and this tab has never been authenticated.
 *   2. `children` — when authenticated (profile present OR
 *      `wasAuthenticated` latched from a previous render).
 *   3. `<Navigate to="/signin?returnTo=…" replace />` — when
 *      unauthenticated, preserving the requested route as a query param.
 *
 * @agentic connections
 */

import { Navigate, useLocation, type Location } from "react-router";
import type { ReactNode } from "react";
import { BootSplash } from "@/app/routes/BootSplash";
import { useAuthSession } from "@/app/auth/AuthSessionContext";
import { sanitizeReturnTo } from "@/app/auth/returnTo";

/**
 * Build the `?returnTo=` query string for the sign-in redirect from the
 * current router location. Skipped (returns "/signin") when the current
 * location is the root or already the sign-in page.
 * @agentic connections
 */
const buildSignInRedirect = (location: Location): string => {
  const target = location.pathname + location.search + location.hash;
  if (target === "/" || location.pathname === "/signin") return "/signin";
  // Sanitize first so we never put an attacker-controlled path into the
  // query string verbatim. The browser bar is user-controlled but we
  // still apply the same guard.
  const safe = sanitizeReturnTo(target);
  if (!safe) return "/signin";
  return `/signin?returnTo=${encodeURIComponent(safe)}`;
};

/**
 * Route element that guards the authenticated tree.
 * @agentic connections
 */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { profile, isLoading, wasAuthenticated } = useAuthSession();
  const location = useLocation();

  if (isLoading && !wasAuthenticated) {
    return <BootSplash />;
  }

  if (profile || wasAuthenticated) {
    return <>{children}</>;
  }

  return <Navigate to={buildSignInRedirect(location)} replace />;
};
